/**
 * Annotation feedback: a semi-transparent overlay you type on.
 *
 * Open it, click anywhere, type a note, click elsewhere for another, Save once
 * for all of them.
 *
 * WHAT IS RECORDED is where typing started, in DOCUMENT coordinates — the
 * position on the page, not on the screen. Nothing about the DOM underneath.
 * The overlay is a sheet drawn over the page, not a targeting mechanism;
 * whatever sits beneath a note is the user's business and they will say so in
 * the text.
 *
 * THE PAGE SCROLLS FREELY and notes travel with it. Document-space
 * coordinates rendered at `y - scrollY` are what make that work: a note stays
 * glued to the content it was written about, and still means the same thing to
 * whoever reads it later.
 *
 * MOUNTED ONCE, OUTSIDE `<Routes>`. Threading a `signedIn` prop into every
 * screen to place this would push session knowledge into routes built
 * deliberately without it — `Capture.tsx` takes no session prop at all. A
 * single mount reads the session App.tsx already resolved, covers every screen
 * including future ones, and does not remount on navigation.
 *
 * POST-AUTH ONLY. Renders nothing without a session, on every route including
 * `/capture` and `/launch`, which themselves render anonymously.
 *
 * ESC IS NOT DESTRUCTIVE. Every keystroke is written to localStorage, so the
 * only two things that can destroy notes are an explicit Discard and a
 * successful Save. See `lib/annotation-draft.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LIMITS } from '@ideabrief/core';
import { Mono } from './ui';
import { screenNameFor } from '../lib/screen-name';
import {
  clearDraftNotes,
  loadDraftNotes,
  saveDraftNotes,
  type DraftNote,
} from '../lib/annotation-draft';

type Phase = 'idle' | 'annotating' | 'saving' | 'confirm-exit' | 'confirm-discard';

/** Width of a note's input, used to keep it inside the viewport. */
const NOTE_W = 240;
const NOTE_H = 96;

export function AnnotationOverlay() {
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [notes, setNotes] = useState<DraftNote[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  /**
   * Live page scroll. Notes are stored in DOCUMENT space and rendered at
   * `y - scrollY`, which is what keeps them attached to content as the page
   * moves under the overlay.
   */
  const [scrollY, setScrollY] = useState(0);

  const screen = screenNameFor(location.pathname);
  const open = phase !== 'idle';
  const withText = notes.filter((n) => n.text.trim().length > 0);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Autosave on every change. This is what makes Esc safe.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(
      () => saveDraftNotes(location.pathname, notes, scrollY),
      300,
    );
    return () => clearTimeout(t);
  }, [notes, open, location.pathname]);

  /**
   * The page scrolls freely while annotating, and notes travel with it.
   *
   * An earlier version locked scrolling, on the reasoning that a fixed overlay
   * over a moving page would leave notes pointing at the wrong thing. That was
   * solving the problem the wrong way round: the fix is to store DOCUMENT
   * coordinates and render them offset by the live scroll position, so a note
   * stays glued to the content it was written about. Locking scroll made the
   * feature unable to annotate anything below the fold — most of every screen.
   *
   * `scrollY` is tracked rather than captured once, and every note's `y` is
   * document-space. The two are combined at render time.
   */
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setScrollY(Math.round(window.scrollY));
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [open]);

  const start = useCallback(() => {
    setScrollY(Math.round(window.scrollY));
    const stashed = loadDraftNotes(location.pathname);
    setNotes(stashed);
    setRestored(stashed.length > 0);
    setActive(null);
    setNotice(null);
    setPhase('annotating');
  }, [location.pathname]);

  /** Leaves without saving. The draft is KEPT — this is not a discard. */
  const stashAndClose = useCallback(() => {
    saveDraftNotes(location.pathname, notes, scrollY);
    setPhase('idle');
    setActive(null);
    if (withText.length > 0) {
      setToast(
        `Kept ${withText.length} note${withText.length === 1 ? '' : 's'} for later.`,
      );
    }
  }, [location.pathname, notes, withText.length]);

  /** The only destructive path, and it is always confirmed. */
  const discard = useCallback(() => {
    clearDraftNotes(location.pathname);
    setNotes([]);
    setActive(null);
    setPhase('idle');
    setToast('Notes discarded.');
  }, [location.pathname]);

  /**
   * Esc asks rather than closing, once there is something to lose.
   *
   * Removing Esc entirely would be worse: the overlay covers the whole app, so
   * a user who wants out will otherwise hunt for a close button while their
   * clicks keep placing carets. The fix is to make the escape hatch
   * non-destructive, not to remove it.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (phase === 'confirm-exit' || phase === 'confirm-discard') {
        setPhase('annotating');
        return;
      }
      // Nothing typed yet: just close. A confirm here would be a nag.
      if (withText.length === 0) {
        setPhase('idle');
        setNotes([]);
        return;
      }
      // Flush before asking. The autosave is debounced, so a user who hits Esc
      // immediately after typing would otherwise be looking at a confirm
      // dialog whose promise ("we'll hold them") the disk does not yet honour.
      saveDraftNotes(location.pathname, notes, scrollY);
      setPhase('confirm-exit');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // `notes` and the path are read inside the handler, so they must be
    // listed — a stale closure here would flush an older set of notes than the
    // one on screen.
  }, [open, phase, withText.length, notes, location.pathname]);

  function placeNote(e: React.MouseEvent<HTMLDivElement>) {
    if (phase !== 'annotating') return;
    // Only a click on the backdrop itself places a note — clicks inside an
    // existing note's box must not spawn another behind it.
    if (e.target !== e.currentTarget) return;
    if (notes.length >= LIMITS.feedbackNotesPerSubmission) {
      setNotice(`That's the most notes we can take at once — save these first.`);
      return;
    }

    // Keep the box on screen when clicking near an edge.
    const x = Math.min(Math.max(8, e.clientX), window.innerWidth - NOTE_W - 8);
    const viewportY = Math.min(
      Math.max(8, e.clientY),
      window.innerHeight - NOTE_H - 8,
    );

    // Stored in DOCUMENT space. A viewport coordinate would mean something
    // different the moment the page scrolled, both on screen and to whoever
    // reads the note later.
    const placed = {
      x: Math.round(x),
      y: Math.round(viewportY + window.scrollY),
      text: '',
    };

    // ABANDONED EMPTY NOTES ARE SWEPT UP HERE.
    //
    // Clicking opens a box; clicking somewhere else means the user changed
    // their mind about the first spot. Leaving the empty box behind would
    // litter the screen with dead frames the user then has to close by hand,
    // and it reads as though a note exists where none does. An empty box is an
    // intention, not content — only text makes it a note.
    //
    // Pruning and appending are ONE state update, because `active` is an index
    // into the array: doing them separately would leave the caret pointing at
    // whatever slid into the old position.
    const kept = notes.filter((n) => n.text.trim().length > 0);
    setNotes([...kept, placed]);
    // Derived from `kept`, not from `notes.length`, so the caret lands on the
    // new box after any empties have been removed.
    setActive(kept.length);
    setNotice(null);
    setRestored(false);
  }

  function updateNote(i: number, text: string) {
    setNotes((prev) => prev.map((n, j) => (j === i ? { ...n, text } : n)));
  }

  function removeNote(i: number) {
    setNotes((prev) => prev.filter((_, j) => j !== i));
    setActive(null);
  }

  async function save() {
    const payload = notes
      .filter((n) => n.text.trim().length > 0)
      .map((n) => ({ x: n.x, y: n.y, text: n.text.trim() }));
    if (payload.length === 0) return;

    // FLUSH THE STASH BEFORE THE REQUEST, not after.
    //
    // The autosave above is debounced, so a save attempted within that window
    // of the last keystroke would find nothing on disk — and if the request
    // then failed and the tab closed, the newest edits would be gone. Writing
    // synchronously here makes the stash current at the exact moment the only
    // fallible step begins.
    saveDraftNotes(location.pathname, notes, scrollY);

    setPhase('saving');
    setNotice(null);

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: location.pathname,
          screen,
          viewportW: window.innerWidth,
          viewportH: window.innerHeight,
          scrollY: scrollY,
          notes: payload,
        }),
      });

      if (res.ok) {
        // Only clear the stash once the server owns it.
        clearDraftNotes(location.pathname);
        setNotes([]);
        setActive(null);
        setPhase('idle');
        setToast(
          `Noted — thanks. ${payload.length} note${payload.length === 1 ? '' : 's'} sent.`,
        );
        return;
      }

      // Every failure below keeps the overlay open and the notes intact, and
      // they are already in localStorage regardless.
      setPhase('annotating');
      if (res.status === 401) {
        setNotice('Your session expired — sign in and save again. Notes are kept.');
      } else if (res.status === 429) {
        setNotice("That's a lot of feedback today — try again tomorrow. Notes are kept.");
      } else {
        setNotice('That did not send. Your notes are safe — try again.');
      }
    } catch {
      setPhase('annotating');
      setNotice('That did not send — check your connection. Notes are kept.');
    }
  }

  if (!open) {
    return (
      <>
        <button
          type="button"
          onClick={start}
          aria-label="Annotate this screen with feedback"
          style={{
            position: 'fixed',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9000,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '6px 13px',
            borderRadius: 999,
            border: '1px solid var(--line)',
            background: 'var(--card)',
            boxShadow: '0 2px 10px rgba(24,23,32,.10)',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{ display: 'grid', gridTemplateColumns: '2px 2px', gap: 2 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                style={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  background: 'var(--muted)',
                }}
              />
            ))}
          </span>
          <Mono size={10}>FEEDBACK</Mono>
        </button>
        {toast && <Toast>{toast}</Toast>}
      </>
    );
  }

  const busy = phase === 'saving';
  const asking = phase === 'confirm-exit' || phase === 'confirm-discard';

  return (
    <div
      role="dialog"
      aria-label="Annotate this screen"
      style={{ position: 'fixed', inset: 0, zIndex: 9000 }}
    >
      {/* The annotation surface. A light wash so the mode is unmistakable
          without distorting the colours being reviewed — the banner below
          carries the "you are annotating" signal. */}
      <div
        onClick={placeNote}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(24,23,32,.10)',
          // A TEXT CARET, not a crosshair. Crosshair reads as "select a
          // region" or "pick a target"; the I-beam is the cursor every user
          // already associates with "you can type here", which is exactly what
          // clicking does.
          cursor: busy || asking ? 'default' : 'text',
        }}
      />

      {/* Mode banner. */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 14px',
          borderRadius: 999,
          background: 'var(--ink)',
          boxShadow: '0 4px 18px rgba(24,23,32,.28)',
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Mono size={10} color="#fff">
          ANNOTATING · {screen.toUpperCase()}
        </Mono>
        <Mono size={10} color="rgba(255,255,255,.6)">
          {withText.length === 0
            ? 'CLICK ANYWHERE TO ADD A NOTE'
            : `${withText.length} NOTE${withText.length === 1 ? '' : 'S'}`}
        </Mono>
        <button
          type="button"
          onClick={() => void save()}
          disabled={withText.length === 0 || busy}
          style={{
            padding: '5px 12px',
            borderRadius: 999,
            border: 'none',
            background: withText.length > 0 ? '#fff' : 'rgba(255,255,255,.18)',
            color: withText.length > 0 ? 'var(--ink)' : 'rgba(255,255,255,.5)',
            font: '500 12.5px/1 inherit',
            cursor: withText.length > 0 && !busy ? 'pointer' : 'default',
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() =>
            withText.length > 0 ? setPhase('confirm-exit') : setPhase('idle')
          }
          style={{
            padding: '5px 8px',
            borderRadius: 999,
            border: 'none',
            background: 'none',
            color: 'rgba(255,255,255,.7)',
            font: '12.5px/1 inherit',
            cursor: 'pointer',
          }}
        >
          Exit
        </button>
      </div>

      {restored && (
        <div
          role="status"
          style={{
            position: 'absolute',
            top: 54,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 12px',
            borderRadius: 999,
            background: 'var(--card)',
            border: '1px solid var(--line)',
          }}
        >
          <Mono size={10}>
            RESTORED {withText.length} UNSAVED NOTE
            {withText.length === 1 ? '' : 'S'}
          </Mono>
        </div>
      )}

      {notice && (
        <div
          role="status"
          style={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '9px 14px',
            borderRadius: 10,
            background: 'var(--card)',
            border: '1px solid var(--line)',
            fontSize: 13,
            color: 'var(--ink2)',
            maxWidth: 'min(420px, calc(100vw - 32px))',
          }}
        >
          {notice}
        </div>
      )}

      {/* The notes themselves. */}
      {notes.map((note, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: note.x,
            // Document space → viewport space. This is what makes a note stay
            // with the content it was written about as the page scrolls.
            top: note.y - scrollY,
            width: NOTE_W,
            background: 'var(--card)',
            border: `1px solid ${active === i ? 'var(--acc)' : 'var(--line)'}`,
            borderRadius: 10,
            boxShadow: '0 4px 18px rgba(24,23,32,.18)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 8px',
              background: 'var(--acc-soft)',
              borderBottom: '1px solid var(--acc-line)',
            }}
          >
            <Mono size={9.5}>NOTE {i + 1}</Mono>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => removeNote(i)}
              aria-label={`Remove note ${i + 1}`}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--muted)',
                fontSize: 14,
                lineHeight: 1,
                padding: 2,
              }}
            >
              ×
            </button>
          </div>
          <textarea
            autoFocus={active === i}
            value={note.text}
            onChange={(e) => updateNote(i, e.target.value)}
            onFocus={() => setActive(i)}
            maxLength={LIMITS.feedbackNote}
            placeholder="What's wrong here?"
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: 'none',
              outline: 'none',
              resize: 'none',
              padding: '8px 10px',
              background: 'var(--card)',
              font: '13.5px/1.45 inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      ))}

      {asking && (
        <ConfirmDialog
          discarding={phase === 'confirm-discard'}
          count={withText.length}
          onKeep={stashAndClose}
          onDiscard={discard}
          onBack={() => setPhase('annotating')}
          onAskDiscard={() => setPhase('confirm-discard')}
        />
      )}
    </div>
  );
}

/**
 * Exit confirmation.
 *
 * The DEFAULT action is Keep, and discarding takes a second explicit step. The
 * user has already typed these notes; the interface should not make throwing
 * them away the easy path.
 */
function ConfirmDialog({
  discarding,
  count,
  onKeep,
  onDiscard,
  onBack,
  onAskDiscard,
}: {
  discarding: boolean;
  count: number;
  onKeep: () => void;
  onDiscard: () => void;
  onBack: () => void;
  onAskDiscard: () => void;
}) {
  const plural = count === 1 ? '' : 's';
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(24,23,32,.34)',
      }}
    >
      <div
        role="alertdialog"
        aria-label={discarding ? 'Discard notes?' : 'Leave annotation mode?'}
        style={{
          width: 'min(360px, calc(100vw - 40px))',
          background: 'var(--card)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: 18,
          boxShadow: '0 14px 48px rgba(24,23,32,.26)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
          {discarding
            ? `Discard ${count} note${plural}?`
            : `Keep your ${count} note${plural}?`}
        </h2>
        <p
          style={{
            margin: '8px 0 16px',
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--muted)',
          }}
        >
          {discarding
            ? "They'll be gone for good. This can't be undone."
            : "We'll hold them on this device. Reopen feedback on this screen and they'll be waiting."}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {discarding ? (
            <>
              <button type="button" onClick={onBack} style={secondaryBtn}>
                Cancel
              </button>
              <button
                type="button"
                onClick={onDiscard}
                style={{ ...primaryBtn, background: 'var(--amber, #b45309)' }}
              >
                Discard
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onAskDiscard} style={secondaryBtn}>
                Discard
              </button>
              <button type="button" onClick={onKeep} style={primaryBtn}>
                Keep them
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const secondaryBtn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 9,
  border: '1px solid var(--line)',
  background: 'none',
  cursor: 'pointer',
  fontSize: 14,
  color: 'var(--muted)',
};

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 9,
  border: 'none',
  background: 'var(--ink)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
};

function Toast({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 22,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9002,
        padding: '10px 16px',
        borderRadius: 999,
        background: 'var(--ink)',
        color: '#fff',
        fontSize: 13.5,
        boxShadow: '0 6px 24px rgba(24,23,32,.22)',
      }}
    >
      {children}
    </div>
  );
}
